import { getAuthorizedCroppingFilter } from "@omnia/shared/fields"
import { StockType, TerraMapLayerType } from "@omnia/shared/generated/enums"
import { getDefaultGeometryFormat, hasBinaryResponses, readGeometriesFromOmniaBinaryToIndex } from "@omnia/shared/geom/binary"
import { NutritionVersionService } from "@omnia/shared/nutrition/version/service"
import { isResourceAccessible } from "@omnia/shared/permissions/holding/accessRestrictions"
import { sortDates } from "@omnia/shared/utils/utils"
import { ClosedPeriodProvider, ClosedPeriodRules, createClosedPeriodProvider } from "../nvz/closedPeriodProvider"
import { convertDatesToJsonUtc, createMap, DateString, FieldViewModel, Geometry2, IFertiliserProduct, INMaxCropGroup, IOmniaSession, IOmniaSettings, isArray, isArrayEmpty, isDate, ISoilUnitService, models, OmniaModuleId, OmniaObjectType, omniaResolve, OmniaService, PlanValidationService, setDates, SharedLayersService, SharedStocksService } from "../omnia"
import { IAppZoneUpdateModel, ICroppingBiomass, ICroppingReqUpdateModel, IDataLayer, IFertiliserStock, IFieldsService, IManureStock, INtrApp, INtrBalance, INtrReq, INtrReqConfig, INutritionPlan, INutritionService, IPlanUpdateModel, IRequirementsProvider, ISoilAnalysis, ISoilTypeService, LayerTypeId, ManureNApp, Notifications, SoilUnit } from "../types"
import { createBalanceProvider } from "./balance"
import { createNutritionCalcService, INutritionCalcService } from "./calcService"
import { setupFromChanges } from "./changes"
import { getManureAnalysisProvider } from "./manureAnalysis"
import { getNotifications } from "./notifications"
import { createRequirementsProvider } from "./requirementsProvider"
import { setupApplications, setupBalance, setupNMaxGroups, setupRequirements } from "./setup"

type IStocksService = SharedStocksService

type ArrayOrDiffs<T> =
    | (Omit<T, "Geometry"> & { Geometry: number })[]
    | {
        Resources: (Omit<T, "Geometry"> & { Geometry: number })[],
        Keys: number[]
    }

interface IServiceData {

    Config: INtrReqConfig[]

    Plans: INutritionPlan[]

    Requirements: ArrayOrDiffs<INtrReq>
    Applications: ArrayOrDiffs<INtrApp>
    OpeningBalance: (Omit<INtrBalance, "Geometry"> & { Geometry: number })[]

    NMaxCropGroups: INMaxCropGroup[]

    ManureApplications: ManureNApp[]

    NvzRules: ClosedPeriodRules[]

    Geometries: { [key: number]: Geometry2 }

    Timestamp: DateString

}

export class NutritionService extends OmniaService implements INutritionService {

    private readonly _settings: IOmniaSettings

    private readonly calcSvc: INutritionCalcService
    private nvzSvc: ClosedPeriodProvider

    private lastRefresh = {
        timestamp: undefined as DateString,
        year: undefined as number
    }

    private config: INtrReqConfig[]
    private requirements: INtrReq[]
    private nmax: INMaxCropGroup[]
    private manureApplications: ManureNApp[]

    private plans: INutritionPlan[]
    private applications: INtrApp[]

    private fertStocks: IFertiliserStock[]
    private manureStocks: IManureStock[]

    private readonly _balance = createBalanceProvider(() => this.loadOpeningBalance())

    private layers: IDataLayer[]
    private biomass: ICroppingBiomass[]

    private soilAnalysis: ISoilAnalysis[]

    private notifications: Notifications

    public planFilter: string

    static $inject = [
        "OmniaFieldsService",
        "OmniaStocksService",
        "OmniaDatalayersService",
        "OmniaSoilTypeService",
        "OmniaSoilUnitService",
        "OmniaSession",
        "OmniaPlanValidationService",
        "OmniaNutritionVersion"
    ]

    constructor(
        private fieldSvc: IFieldsService,
        private stockSvc: IStocksService,
        private datalayerSvc: SharedLayersService,
        private _soilTypeService: ISoilTypeService,
        private _soilUnitSvc: ISoilUnitService,
        session: IOmniaSession,
        readonly planValidationService: PlanValidationService,
        private readonly version: NutritionVersionService
    ) {

        super(session, {
            name: "Nutrition",
            module: OmniaModuleId.Nutrition,
            dependencies: [fieldSvc, stockSvc, datalayerSvc]
        })

        this._settings = session.getSettings("nutrition2")

        this.calcSvc = createNutritionCalcService(this.manureAnalysis)

    }

    /**
     * This decides if the whole module is read-only in the current year
     * As in other modules, this is based on the user's permissions (super.readOnly),
     * but also in the version of the module enabled
     * If v2 is enabled in the current year, then v1 is read-only
     */
    get readOnly() {
        return super.readOnly || this.session.isReadOnly(p => p.nutritionPlans) || (this.version.getCurrentVersion() === 2)
    }

    get fields(): FieldViewModel[] {
        return this.fieldSvc.fields
    }

    get cropping(): FieldViewModel[] {
        return this.fieldSvc.cropping
    }

    getAuthorizedCroppingFilter() {
        return getAuthorizedCroppingFilter(this.fieldSvc)
    }

    get settings(): IOmniaSettings {
        return this._settings
    }

    get manureAnalysis() {
        return getManureAnalysisProvider(this.soilTypeService, this.holding.Region)
    }

    get closedPeriodService(): ClosedPeriodProvider {
        return this.nvzSvc
    }

    get soilTypeService(): ISoilTypeService {
        return this._soilTypeService
    }

    get calculationService(): INutritionCalcService {
        return this.calcSvc
    }

    getFertiliserStocks(): IFertiliserStock[] {
        return this.fertStocks
    }

    getFertiliserProducts(): IFertiliserProduct[] {
        return this.stockSvc.getFertiliserProducts()
    }

    getManureStocks(): IManureStock[] {
        return this.manureStocks
    }

    getConfig(): INtrReqConfig[] {
        return this.config.slice()
    }

    getPlans() {
        return [...this.plans]
    }

    getRequirements(): INtrReq[] {
        return this.requirements.slice()
    }

    getRequirementsProvider(): IRequirementsProvider {
        return createRequirementsProvider(this.requirements, this.plans)
    }

    getApplications() {
        return [...this.applications]
    }

    get balance() {
        return this._balance.provider
    }

    getManureApplications(filter: (a: ManureNApp) => boolean) {
        return this.manureApplications.filter(filter)
    }

    getNMaxCropGroups(): INMaxCropGroup[] {
        return this.nmax ? this.nmax.slice() : []
    }

    getNMaxCroppingGroup(croppingId: number): INMaxCropGroup {
        if (this.nmax) {
            return this.nmax.find(n => n.Cropping && n.Cropping.some(c => c.CroppingId == croppingId))
        }
    }

    getDataLayerType(typeId: LayerTypeId) {
        return this.datalayerSvc.getLayerTypes().find(lt => lt.TypeId == typeId)
    }

    getSoilUnit(): SoilUnit {
        return this._soilUnitSvc.soil
    }

    getNotifications() {
        return this.notifications
    }

    getRefreshPromise(): ng.IPromise<any> {

        this.layers = null
        this.biomass = null
        this.soilAnalysis = null

        return this.loadServiceData()

    }

    shouldRefresh() {

        const changes = this.apiState.getChangesFromSource(
            this._changeNumber,
            s => (s == OmniaModuleId.Nutrition)
                || (s == OmniaModuleId.Fields)
                || (s == OmniaModuleId.Layers)
                || (s == OmniaModuleId.Stocks)
                || (s == OmniaModuleId.Soil)
                || (s == OmniaModuleId.Holding)
                || (s == OmniaModuleId.FieldPerformance)
                || (s == "refresh-all")
                || (!s)
        )

        return !isArrayEmpty(changes)

    }

    private loadServiceData(): ng.IPromise<any> {

        type Loader = () => ng.IPromise<IServiceData>

        const startTime = new Date(Date.now())

        const { holdingId, yearId, ohttp, session, lastRefresh } = this

        const partial = Boolean(lastRefresh.timestamp) && (lastRefresh.year === yearId)
        const since = partial ? lastRefresh.timestamp : undefined
        const geometryFormat = getDefaultGeometryFormat()

        let url = `/api/nutrition/v4/service/data?holdingId=${holdingId}&yearId=${yearId}`

        if (since) {
            url = `${url}&since=${encodeURIComponent(since)}`
        }

        if (geometryFormat) {
            url = `${url}&geometryFormat=${geometryFormat}`
        }


        // JSON responses
        const loadAsJson: Loader = () => {
            return ohttp.get(url).then(r => r.data as IServiceData)
        }

        // Binary responses
        const loadAsBinary: Loader = () => {
            return this.ohttp.getArrayBuffer(url).then(buffer => readGeometriesFromOmniaBinaryToIndex<IServiceData>(buffer))
        }

        const load = hasBinaryResponses(geometryFormat)
            ? loadAsBinary
            : loadAsJson

        const region = this.holding.Region
        this.biomass = undefined

        return load().then(data => {

            const geometries = data.Geometries || {}

            this.fertStocks = setupFertiliserStocks(this.stockSvc.getFertiliserStocks())
            this.manureStocks = setupManureStocks(this.stockSvc.getManureStocks())

            this.config = this.setupConfig(data.Config)
            this.plans = this.setupPlans(data.Plans)

            const croppingProvider = this.fieldSvc.getCroppingProvider()

            const requirements = setupRequirements(setupGeometries(data.Requirements, geometries), croppingProvider)
            this.requirements = partial && (!isArray(data.Requirements))
                ? setupFromChanges(this.requirements, requirements, data.Requirements.Keys, r => r.NtrReqId)
                : requirements

            const applications = setupApplications(setupGeometries(data.Applications, geometries), croppingProvider, this.plans)
            this.applications = partial && (!isArray(data.Applications))
                ? setupFromChanges(this.applications, applications, data.Applications.Keys, r => r.NtrAppId)
                : applications

            this.nmax = setupNMaxGroups(data.NMaxCropGroups)
            this.manureApplications = setDates(data.ManureApplications || [], ["ApplicationDate"], true)

            const balance = setupBalance(setupGeometries(data.OpeningBalance, geometries), croppingProvider)

            this._balance.reset(balance)

            this.nvzSvc = createClosedPeriodProvider(data.NvzRules, this.soilTypeService, region)

            this.notifications = getNotifications(
                this.fieldSvc.cropping.filter(c => c.currentYear),
                this.requirements
            )

            const timestamp = data.Timestamp
                ? data.Timestamp
                : (lastRefresh.timestamp ?? startTime.toISOString())

            this.lastRefresh = {
                timestamp,
                year: yearId
            }

        })

    }

    private loadOpeningBalance() {

        const { holdingId, yearId, ohttp } = this

        const url = `/api/nutrition/v1/balance/opening?holdingId=${holdingId}&yearId=${yearId}`

        return ohttp.get(url).then(r => {

            let data: IServiceData = r.data

            const geometries = data.Geometries || {}

            const croppingProvider = this.fieldSvc.getCroppingProvider()

            const balance = setupBalance(setupGeometries(data.OpeningBalance, geometries), croppingProvider)

            return balance

        })

    }

    private setupConfig(config: INtrReqConfig[]) {
        setDates(config, ["CreatedOn", "UpdatedOn"], false)
        return config
    }

    private setupPlans(plans: INutritionPlan[]) {

        const { session } = this


        setDates(plans, ["CreatedOn"], false)

        const fertiliserStocks = createMap(this.fertStocks, s => s.FertiliserStockId)
        const manureStocks = createMap(this.manureStocks, s => s.ManureStockId)

        plans.forEach(p => {

            p.Applications = p.Applications || []
            p.Applications.forEach(a => {

                a.Fertiliser = fertiliserStocks.get(a.FertiliserId)
                a.Manure = manureStocks.get(a.ManureId)

                setDates([a], ["ApplicationDate", "ActualDate"], false)

            })

            p.Cropping = p.Cropping || []

            p.Restricted = !isResourceAccessible(session, p)

        })

        return plans

    }

    getExclusiveAccess() {
        const url = "/api/nutrition/lock"
        return this.put(url, undefined, false).then(_ => true).catch(_ => false)
    }

    releaseExclusiveAccess() {
        const url = "/api/nutrition/lock"
        return this.delete(url, undefined, false)
    }

    getNutritionLayers() {

        if (this.layers) {
            return omniaResolve(this.layers.slice())
        }

        const types = [
            LayerTypeId.SoilAnalysis,
            LayerTypeId.SoilType,
            LayerTypeId.SubsoilType,
            LayerTypeId.Yield,
            LayerTypeId.P2O5,
            LayerTypeId.K2O,
            LayerTypeId.MgO,
            LayerTypeId.pH
        ]

        return this.datalayerSvc.getHoldingLayers(types).then(layers => {

            this.layers = layers.filter(l => (!l.TerraMap) || (l.TerraMap == TerraMapLayerType.Raw))

            return this.layers

        })

    }

    getBiomassLayers() {

        if (this.biomass) {
            return omniaResolve(this.biomass.slice())
        }

        const url = `/api/nutrition/v2/requirements/biomass/?holdingId=${this.holdingId}&yearId=${this.yearId}`

        return this.ohttp.get(url).then(r => {

            let biomass: ICroppingBiomass[] = r.data || []

            biomass = biomass.sort((a, b) => sortDates(b.BiomassDate || b.LayerDate, a.BiomassDate || a.LayerDate))

            biomass = setDates(biomass, ["BiomassDate", "LayerDate"], false)

            this.biomass = biomass = biomass.sort((a, b) => sortDates(b.LayerDate, a.LayerDate))

            this.biomass.forEach(b => {

                b.Name = getBiomassLayerName(b)

                b.Values = (b.Values || []).map(v => ({
                    ...v,
                    Value: v.Value || 0
                }))

            })


            return this.biomass

        })

    }

    getSoilAnalysisUsedForRequirements() {

        if (this.soilAnalysis) {
            return omniaResolve(this.soilAnalysis.slice())
        }

        const url = `/api/nutrition/v2/requirements/soil-analysis/?holdingId=${this.holdingId}&yearId=${this.yearId}`

        return this.ohttp.get(url).then(r => {

            let soilAnalysis: ISoilAnalysis[] = r.data

            this.soilAnalysis = setDates(soilAnalysis, ["SamplingDate"], true)

            return this.soilAnalysis

        })

    }


    calculateRequirements(config: INtrReqConfig[]): ng.IPromise<any> {

        const url = "/api/nutrition/v2/requirements/calculate"

        const data = {
            holdingId: this.holdingId,
            year: this.yearId,
            content: config
        }

        return this.post(url, data, { type: OmniaObjectType.NutritionRequirement })

    }

    updateRequirements(updates: ICroppingReqUpdateModel[]): ng.IPromise<INtrReq[]> {

        const url = "/api/nutrition/v2/requirements/update"

        const data = {
            holdingId: this.holdingId,
            year: this.yearId,
            content: updates
        }

        return this.post(url, data, false).then((req: INtrReq[]) => {

            const croppingProvider = this.fieldSvc.getCroppingProvider()

            req = setupRequirements(req, croppingProvider)

            // For all crops that have been updated, the endpoint returns a full list of their
            // recalculated initial requirements, which should replace the current ones
            // This is done by excluding all initial requirements for those crops,
            // then adding back the ones returned by the endpoint
            // All further requirements (i.e., associated with a plan) are kept as they are at this stage,
            // they will be updated later, when the plans are recalculated

            const cropping = new Set(updates.filter(u => u.CroppingId).map(u => u.CroppingId))

            this.requirements = this.requirements
                .filter(r => r.PlanId || (!cropping.has(r.CroppingId)))
                .concat(req)

            return req

        })

    }

    resetRequirements(croppingIds: number[], requirements: boolean, balance: boolean): ng.IPromise<any> {

        const data = {
            holdingId: this.holdingId,
            year: this.yearId,
            croppingIds,
            requirements,
            balance
        }

        const url = "/api/nutrition/v2/requirements/reset"

        return this.post(url, data, { type: OmniaObjectType.NutritionRequirement })

    }

    calculateCroppingPlans(croppingIds: number[], recalculateAll: boolean): ng.IPromise<any> {

        const data = {
            holdingId: this.holdingId,
            year: this.yearId,
            croppingIds,
            recalculateAll
        }

        const url = "/api/nutrition/v2/app/calculate/cropping"

        return this.post(url, data)

    }

    savePlan(plan: IPlanUpdateModel): ng.IPromise<any> {

        const model = {
            Plan: preparePlanForUpdate(plan.plan),
            Recalculate: plan.recalculate,
            ResetEdits: plan.resetEdits
        }

        const url = `/api/nutrition/v2/app/plan/save`

        return this.post(url, model).then((planId: number) => planId)

    }

    deletePlan(planId: number): ng.IPromise<any> {

        const url = `/api/nutrition/v2/app/plan/delete?planId=${planId}`

        return this.delete(url)

    }

    updateApplicationZones(zones: IAppZoneUpdateModel[]): ng.IPromise<any> {

        const url = `/api/nutrition/v2/app/zones/update`

        const data = {
            holdingId: this.holdingId,
            year: this.yearId,
            content: zones
        }

        return this.post(url, data)

    }

    isCroppingInNutritionPlans(croppingId: number): boolean {
        return this.plans.some(p => p.Cropping && p.Cropping.some(pc => pc.CroppingId == croppingId))
    }

}

function preparePlanForUpdate(plan: INutritionPlan): INutritionPlan {

    plan = angular.copy(plan)

    delete plan.Restricted

    convertDatesToJsonUtc(plan)

    plan.Applications.forEach(a => {

        if (a.FertiliserId) {
            a.Fertiliser = null
        }

        if (a.Fertiliser) {
            a.Fertiliser = {
                StandardProductId: a.Fertiliser.StandardProductId,
                StockId: 0,
                StockType: StockType.Fertiliser,
                HoldingId: a.Fertiliser.HoldingId,
                YearId: a.Fertiliser.YearId,
                Name: a.Fertiliser.Name,
            }
            a.FertiliserId = 0
        }

        if (a.ManureId) {
            a.Manure = null
        }

        if (a.Manure) {
            a.Manure = {
                ManureTypeId: a.Manure.ManureTypeId,
                ManureType: null,
                StockId: 0,
                StockType: StockType.Manure,
                HoldingId: a.Manure.HoldingId,
                YearId: a.Manure.YearId,
                Name: a.Manure.Name,
            }
        }

    })

    return plan

}

function setupGeometries<T extends { Geometry: number }>(items: T[] | { Resources: T[] }, index: { [key: number]: Geometry2 }) {

    const array = isArray(items)
        ? items
        : items.Resources

    return array.map(i => ({
        ...i,
        Geometry: index[i.Geometry]
    }))

}

function getBiomassLayerName(b: ICroppingBiomass): string {

    // If this was created from satellite imagery, simply return the stored name
    if (b.SourceImageType) {
        return b.LayerName
    }

    if (b.LayerName?.startsWith("Plant Vision")) {
        return b.LayerName
    }

    const date = b.BiomassDate || b.LayerDate

    if (isDate(date)) {

        const prefix = b.Unit
            ? models.BiomassUnit[b.Unit]
            : "Biomass"

        return `${prefix} - ${date.toLocaleDateString()}`

    }

    return b.LayerName || "Biomass"

}


/**
 * Converts fertiliser stocks for them to be usable in this context
 * If a fertiliser stock is not custom, but has no standard product set,
 * it implies it references a custom product
 * In the context of this module, it must be treated as a custom stock
 */
function setupFertiliserStocks(stocks: IFertiliserStock[]): IFertiliserStock[] {


    return (stocks ?? []).map(s => s.CustomProduct
        ? {
            ...s,
            Custom: true
        }
        : s
    )
}

/**
 * Converts manure stocks for them to be usable in this context
 * All such stocks are handled as custom stocks in the context of this module
 */
function setupManureStocks(stocks: IManureStock[]): IManureStock[] {

    return (stocks ?? []).map(s => s.CustomProduct
        ? {
            ...s,
            Custom: true
        }
        : s
    )
}
